import { z } from "zod";

import {
  COMMERCIAL_CATALOG_GENERATION,
  COMMERCIAL_EXTRA_USAGE_BLOCK,
  COMMERCIAL_PLAN_CATALOG,
  getCommercialPlan,
  isCommercialPlanKey,
} from "./catalog";
import {
  CommercialMigrationState,
  CommercialMeterKey,
  type CommercialAllowances,
  type CommercialCatalogSnapshot,
  type CommercialPlanDefinition,
  type CommercialPlanPresentationDefinition,
  type CommercialPlanSnapshot,
} from "./types";

const commercialUsageAmountsSchema = z.object({
  [CommercialMeterKey.UserRequests]: z.number().nonnegative(),
  [CommercialMeterKey.NewNotes]: z.number().nonnegative(),
  [CommercialMeterKey.ActiveNotes]: z.number().nonnegative(),
  [CommercialMeterKey.OpenTasksProjects]: z.number().nonnegative(),
  [CommercialMeterKey.ActiveCustomRoutines]: z.number().nonnegative(),
  [CommercialMeterKey.RoutineExecutions]: z.number().nonnegative(),
  [CommercialMeterKey.ActiveConnections]: z.number().nonnegative(),
  [CommercialMeterKey.VoiceModeHours]: z.number().nonnegative(),
});

const commercialCatalogSchema = z.object({
  extra_usage_block: z.object({
    display_name: z.string().min(1),
    increments: commercialUsageAmountsSchema,
    key: z.string().min(1),
    monthly_price_cents: z.number().int().positive(),
  }),
  extra_usage_available: z.boolean().default(false),
  generation: z.string().min(1),
  plan_management_available: z.boolean(),
  plans: z.array(
    z.object({
      allowances: commercialUsageAmountsSchema,
      checkout_available: z.boolean(),
      display_name: z.string().min(1),
      key: z.string().min(1),
      monthly_price_cents: z.number().int().nonnegative(),
      position: z.number().int().nonnegative(),
      supports_paid_extra_usage: z.boolean(),
    })
  ),
  usage_enforcement_enabled: z.boolean(),
});

const adaptAllowances = (
  amounts: z.infer<typeof commercialUsageAmountsSchema>
): CommercialAllowances => ({
  [CommercialMeterKey.UserRequests]: amounts[CommercialMeterKey.UserRequests],
  [CommercialMeterKey.NewNotes]: amounts[CommercialMeterKey.NewNotes],
  [CommercialMeterKey.ActiveNotes]: amounts[CommercialMeterKey.ActiveNotes],
  [CommercialMeterKey.OpenTasksProjects]: amounts[CommercialMeterKey.OpenTasksProjects],
  [CommercialMeterKey.ActiveCustomRoutines]: amounts[CommercialMeterKey.ActiveCustomRoutines],
  [CommercialMeterKey.RoutineExecutions]: amounts[CommercialMeterKey.RoutineExecutions],
  [CommercialMeterKey.ActiveConnections]: amounts[CommercialMeterKey.ActiveConnections],
  [CommercialMeterKey.VoiceModeHours]: amounts[CommercialMeterKey.VoiceModeHours],
});

const allowancesMatch = (left: CommercialAllowances, right: CommercialAllowances): boolean =>
  Object.values(CommercialMeterKey).every(key => left[key] === right[key]);

const planMatchesPresentationCatalog = (
  plan: CommercialPlanDefinition,
  expectedPlan: CommercialPlanPresentationDefinition
): boolean =>
  plan.key === expectedPlan.key &&
  plan.displayName === expectedPlan.displayName &&
  plan.monthlyPriceCents === expectedPlan.monthlyPriceCents &&
  plan.position === expectedPlan.position &&
  plan.supportsPaidExtraUsage === expectedPlan.supportsPaidExtraUsage &&
  allowancesMatch(plan.allowances, expectedPlan.allowances);

/**
 * Validates the authoritative server catalog against the client presentation
 * contract. A new generation or changed allowance must ship with an explicit
 * client update before checkout is offered.
 */
export const parseCommercialCatalog = (value: unknown): CommercialCatalogSnapshot | null => {
  const parsed = commercialCatalogSchema.safeParse(value);
  if (!parsed.success || parsed.data.generation !== COMMERCIAL_CATALOG_GENERATION) {
    return null;
  }

  const sortedPlans = [...parsed.data.plans].sort((left, right) => left.position - right.position);
  if (sortedPlans.length !== COMMERCIAL_PLAN_CATALOG.length) {
    return null;
  }

  const seenPlanKeys = new Set<string>();
  const plans: CommercialPlanDefinition[] = [];
  for (const plan of sortedPlans) {
    if (!isCommercialPlanKey(plan.key) || seenPlanKeys.has(plan.key)) {
      return null;
    }

    const expectedPlan = getCommercialPlan(plan.key);
    if (!expectedPlan) {
      return null;
    }

    const adaptedPlan: CommercialPlanDefinition = {
      allowances: adaptAllowances(plan.allowances),
      checkoutAvailable: plan.checkout_available,
      displayName: plan.display_name,
      key: plan.key,
      monthlyPriceCents: plan.monthly_price_cents,
      position: plan.position,
      shortName: expectedPlan.shortName,
      supportsPaidExtraUsage: plan.supports_paid_extra_usage,
    };
    if (!planMatchesPresentationCatalog(adaptedPlan, expectedPlan)) {
      return null;
    }

    seenPlanKeys.add(plan.key);
    plans.push(adaptedPlan);
  }

  const extraUsageBlock = parsed.data.extra_usage_block;
  const adaptedExtraUsageAllowances = adaptAllowances(extraUsageBlock.increments);
  if (
    extraUsageBlock.key !== COMMERCIAL_EXTRA_USAGE_BLOCK.key ||
    extraUsageBlock.display_name !== COMMERCIAL_EXTRA_USAGE_BLOCK.displayName ||
    extraUsageBlock.monthly_price_cents !== COMMERCIAL_EXTRA_USAGE_BLOCK.monthlyPriceCents ||
    !allowancesMatch(adaptedExtraUsageAllowances, COMMERCIAL_EXTRA_USAGE_BLOCK.allowances)
  ) {
    return null;
  }

  return {
    extraUsageBlock: {
      allowances: adaptedExtraUsageAllowances,
      displayName: extraUsageBlock.display_name,
      key: extraUsageBlock.key,
      monthlyPriceCents: extraUsageBlock.monthly_price_cents,
    },
    extraUsageAvailable: parsed.data.extra_usage_available,
    generation: parsed.data.generation,
    planManagementAvailable: parsed.data.plan_management_available,
    plans,
    usageEnforcementEnabled: parsed.data.usage_enforcement_enabled,
  };
};

export const getCompatibleCommercialCatalog = ({
  catalog,
  snapshot,
}: {
  catalog: CommercialCatalogSnapshot | null;
  snapshot: Pick<CommercialPlanSnapshot, "catalogGeneration"> | null;
}): CommercialCatalogSnapshot | null => {
  if (!catalog || !snapshot || catalog.generation !== snapshot.catalogGeneration) {
    return null;
  }

  return catalog;
};

/**
 * Returns whether this account must use the new commercial purchase journey.
 *
 * PlatformInfo includes an implicit Mem snapshot before enrollment so usage can
 * be observed in shadow mode. Only the persisted one-way migration state can
 * retire the existing checkout path; catalog-wide switches are not account
 * enrollment authority.
 */
export const isCommercialRolloutActive = ({
  snapshot,
}: {
  catalog: CommercialCatalogSnapshot | null;
  snapshot: Pick<CommercialPlanSnapshot, "catalogGeneration" | "migration"> | null;
}): boolean => snapshot?.migration?.state === CommercialMigrationState.NewCatalogActive;
